import { useEffect, useMemo, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  ArrowRight,
  ArrowLeft,
  CaretDown,
  ChartBar,
  CheckCircle,
  Database,
  DownloadSimple,
  GlobeHemisphereWest,
  GenderFemale,
  GenderMale,
  House,
  Hospital,
  Info,
  List,
  MapPin,
  PoliceCar,
  ShareNetwork,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from "@phosphor-icons/react";
import { featurePath, makeProjector } from "./map-utils";
import type { AtlasIndex, FeatureCollection, InfrastructureIndex, InfrastructurePoint, InfrastructurePointIndex, InfrastructureSummary, Language, Province } from "./types";
import DataExplorer from "./data-explorer";

gsap.registerPlugin(useGSAP);

const copy = {
  en: {
    explore: "Explore",
    data: "Data sources",
    method: "Methodology",
    about: "About",
    eyebrow: "Nepal Census 2021 · Open public atlas",
    titleA: "Understand Nepal,",
    titleB: "one place at a time.",
    intro: "A free, bilingual atlas that turns official census records into clear local stories.",
    exploreData: "Explore the data",
    browseSources: "Browse sources",
    verified: "Source-linked and verified",
    nationalSnapshot: "National snapshot",
    snapshotBody: "A clear starting point for Nepal's people, households and places.",
    population: "Population",
    households: "Households",
    sexRatio: "Sex ratio",
    density: "People per km²",
    choosePlace: "Choose a place",
    chooseBody: "Move from Nepal to a province, district, local level and ward.",
    province: "Province",
    district: "District",
    localLevel: "Local level",
    ward: "Ward",
    allNepal: "All Nepal",
    selectDistrict: "Select district",
    selectLocal: "Select local level",
    selectWard: "Select ward",
    locationFocus: "Location focus",
    national: "Nepal",
    people: "people",
    male: "Male",
    female: "Female",
    localLevels: "Local levels",
    wards: "Wards",
    share: "Share view",
    copied: "Link copied",
    source: "Official source",
    sourceText: "National Population and Housing Census 2021",
    coverage: "Coverage at a glance",
    coverageBody: "Explore the structure behind the data before opening a detailed dataset.",
    provinces: "Provinces",
    districts: "Districts",
    datasets: "Data themes",
    sourceRecords: "source records",
    updated: "Data snapshot built",
    creator: "Created by Sudip Bhatta",
    nonprofit: "An independent, non-profit public data project.",
    openAtlas: "Open Census Atlas",
    filters: "Filters",
    comparison: "Population by province",
    comparisonBody: "A direct comparison using aggregated local-level census records.",
    wardNote: "Ward total is active. Ward-comparable charts below highlight this ward automatically.",
    healthFacilities: "Health facilities",
    policeUnits: "Police units",
    householdCount: "Households",
    householdNote: "Local-profile household roll-up",
    mappedRoads: "Mapped roads",
    waterways: "Mapped waterways",
    majorRoute: "Longest named route",
    majorRiver: "Longest named river",
    noNamedRoute: "No named route in this reference layer",
    noNamedRiver: "No named river in this reference layer",
    chooseDistrictMap: "Choose a district on the map",
    chooseLocalMap: "Choose a local level on the map",
    selectedBoundary: "Selected boundary",
    infrastructureNote: "Spatially matched public infrastructure reference layers",
  },
  ne: {
    explore: "अन्वेषण",
    data: "डाटा स्रोत",
    method: "कार्यविधि",
    about: "बारेमा",
    eyebrow: "नेपाल जनगणना २०७८ · खुला सार्वजनिक एटलस",
    titleA: "नेपाललाई बुझ्नुहोस्,",
    titleB: "एक पटकमा एक ठाउँ।",
    intro: "आधिकारिक जनगणना तथ्याङ्कलाई स्पष्ट स्थानीय कथामा बदल्ने निःशुल्क द्विभाषी एटलस।",
    exploreData: "डाटा अन्वेषण गर्नुहोस्",
    browseSources: "स्रोतहरू हेर्नुहोस्",
    verified: "स्रोतसहित प्रमाणित",
    nationalSnapshot: "राष्ट्रिय झलक",
    snapshotBody: "नेपालका मानिस, घरपरिवार र स्थान बुझ्ने स्पष्ट सुरुवात।",
    population: "जनसंख्या",
    households: "घरपरिवार",
    sexRatio: "लैङ्गिक अनुपात",
    density: "प्रति वर्ग कि.मि. मानिस",
    choosePlace: "स्थान छान्नुहोस्",
    chooseBody: "नेपालबाट प्रदेश, जिल्ला, स्थानीय तह र वडासम्म जानुहोस्।",
    province: "प्रदेश",
    district: "जिल्ला",
    localLevel: "स्थानीय तह",
    ward: "वडा",
    allNepal: "सम्पूर्ण नेपाल",
    selectDistrict: "जिल्ला छान्नुहोस्",
    selectLocal: "स्थानीय तह छान्नुहोस्",
    selectWard: "वडा छान्नुहोस्",
    locationFocus: "छानिएको स्थान",
    national: "नेपाल",
    people: "जना",
    male: "पुरुष",
    female: "महिला",
    localLevels: "स्थानीय तह",
    wards: "वडा",
    share: "दृश्य सेयर गर्नुहोस्",
    copied: "लिङ्क प्रतिलिपि भयो",
    source: "आधिकारिक स्रोत",
    sourceText: "राष्ट्रिय जनगणना २०७८",
    coverage: "कभरेज एक नजरमा",
    coverageBody: "विस्तृत डाटासेट खोल्नुअघि डाटाको संरचना बुझ्नुहोस्।",
    provinces: "प्रदेश",
    districts: "जिल्ला",
    datasets: "डाटा विषय",
    sourceRecords: "स्रोत रेकर्ड",
    updated: "डाटा स्न्यापसट तयार",
    creator: "सुदिप भट्टाद्वारा निर्मित",
    nonprofit: "एक स्वतन्त्र, गैर-नाफामूलक सार्वजनिक डाटा परियोजना।",
    openAtlas: "खुला जनगणना एटलस",
    filters: "फिल्टर",
    comparison: "प्रदेशअनुसार जनसंख्या",
    comparisonBody: "स्थानीय तहका जनगणना रेकर्डको योगमा आधारित तुलना।",
    wardNote: "वडाको कुल तथ्याङ्क सक्रिय छ। तलका वडासँग तुलना गर्न मिल्ने चार्टमा यो वडा स्वतः हाइलाइट हुन्छ।",
    healthFacilities: "स्वास्थ्य संस्था",
    policeUnits: "प्रहरी एकाइ",
    householdCount: "घरपरिवार",
    householdNote: "स्थानीय तह प्रोफाइलको योग",
    mappedRoads: "नक्साङ्कित सडक",
    waterways: "नक्साङ्कित जलमार्ग",
    majorRoute: "सबैभन्दा लामो नामयुक्त मार्ग",
    majorRiver: "सबैभन्दा लामो नामयुक्त नदी",
    noNamedRoute: "यस सन्दर्भ तहमा नामयुक्त मार्ग छैन",
    noNamedRiver: "यस सन्दर्भ तहमा नामयुक्त नदी छैन",
    chooseDistrictMap: "नक्साबाट जिल्ला छान्नुहोस्",
    chooseLocalMap: "नक्साबाट स्थानीय तह छान्नुहोस्",
    selectedBoundary: "छानिएको सिमाना",
    infrastructureNote: "स्थानअनुसार मिलाइएका सार्वजनिक पूर्वाधार सन्दर्भ तहहरू",
  },
} as const;

const number = (value: number, language: Language) =>
  new Intl.NumberFormat(language === "ne" ? "ne-NP" : "en-US", { maximumFractionDigits: 2 }).format(value);

function useAtlasData() {
  const [atlas, setAtlas] = useState<AtlasIndex | null>(null);
  const [map, setMap] = useState<FeatureCollection | null>(null);
  const [infrastructure, setInfrastructure] = useState<InfrastructureIndex | null>(null);
  const [infrastructurePoints, setInfrastructurePoints] = useState<InfrastructurePointIndex | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch("/data/atlas-index.json").then((response) => response.json()),
      fetch("/data/provinces.geojson").then((response) => response.json()),
      fetch("/data/infrastructure.json").then((response) => response.json()),
      fetch("/data/infrastructure-points.json").then((response) => response.json()),
    ])
      .then(([atlasData, mapData, infrastructureData, infrastructurePointData]) => {
        setAtlas(atlasData);
        setMap(mapData);
        setInfrastructure(infrastructureData);
        setInfrastructurePoints(infrastructurePointData);
      })
      .catch(() => setError(true));
  }, []);

  return { atlas, map, infrastructure, infrastructurePoints, error };
}

function NepalMap({
  map,
  selectedProvince,
  onSelect,
  provinceNames,
  instanceId,
}: {
  map: FeatureCollection;
  selectedProvince: string;
  onSelect: (id: string) => void;
  provinceNames: (id: string) => string;
  instanceId: string;
}) {
  const project = useMemo(() => makeProjector(map), [map]);

  return (
    <svg className="nepal-map" viewBox="0 0 900 420" role="img" aria-labelledby={`map-title-${instanceId} map-description-${instanceId}`}>
      <title id={`map-title-${instanceId}`}>Interactive map of Nepal's seven provinces</title>
      <desc id={`map-description-${instanceId}`}>Choose a province to update the census summary and available districts.</desc>
      <defs>
        <filter id={`map-shadow-${instanceId}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#1c2b2a" floodOpacity="0.12" />
        </filter>
        <filter id={`map-glow-${instanceId}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g filter={`url(#map-shadow-${instanceId})`}>
        {map.features.map((feature) => {
          const id = String(feature.properties.FIRST_STAT ?? feature.properties.fid);
          const active = selectedProvince === id;
          const path = featurePath(feature, project);
          return (
            <g key={id}>
              <path
                id={`province-path-${instanceId}-${id}`}
                d={path}
                className={`province-shape ${active ? "is-active" : ""}`}
                tabIndex={0}
                role="button"
                aria-label={provinceNames(id)}
                aria-pressed={active}
                onClick={() => onSelect(active ? "" : id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") onSelect(active ? "" : id);
                }}
              />
              <path d={path} className={`map-tracer ${active || !selectedProvince ? "is-visible" : ""}`} filter={`url(#map-glow-${instanceId})`} aria-hidden="true" />
              {active && <circle className="map-spark" r="4"><animateMotion dur="5.5s" repeatCount="indefinite" rotate="auto"><mpath href={`#province-path-${instanceId}-${id}`} /></animateMotion></circle>}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function DrilldownBoundaryMap({
  boundary,
  name,
  instanceId,
  prompt,
  onSelect,
  points,
}: {
  boundary: FeatureCollection;
  name: string;
  instanceId: string;
  prompt: string;
  onSelect?: (id: string) => void;
  points: InfrastructurePoint[];
}) {
  const project = useMemo(() => makeProjector(boundary), [boundary]);
  const [hoveredName, setHoveredName] = useState("");
  const mapRoot = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add("(prefers-reduced-motion: no-preference)", () => {
      const shapes = mapRoot.current?.querySelectorAll(".drilldown-shape");
      if (!shapes?.length) return;
      gsap.fromTo(
        shapes,
        { autoAlpha: 0, scale: 0.92, transformOrigin: "50% 50%" },
        { autoAlpha: 1, scale: 1, duration: 0.72, stagger: { amount: 0.34 }, ease: "power3.out", clearProps: "transform,visibility,opacity" },
      );
    });
    return () => media.revert();
  }, { dependencies: [boundary], scope: mapRoot, revertOnUpdate: true });

  return (
    <div ref={mapRoot} className="drilldown-map-wrap">
      <div className="map-selection-prompt"><MapPin size={15} weight="fill" /><span>{hoveredName || prompt}</span></div>
      <svg className="nepal-map focus-map" viewBox="0 0 900 420" role="img" aria-labelledby={`map-title-${instanceId} map-description-${instanceId}`}>
        <title id={`map-title-${instanceId}`}>{name} census boundary</title>
        <desc id={`map-description-${instanceId}`}>{prompt}</desc>
        <defs>
          <filter id={`focus-shadow-${instanceId}`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="#1c2b2a" floodOpacity="0.15" />
          </filter>
          <filter id={`focus-glow-${instanceId}`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <g filter={`url(#focus-shadow-${instanceId})`}>
          {boundary.features.map((feature, index) => {
            const path = featurePath(feature, project);
            const atlasId = String(feature.properties.atlasId ?? "");
            const atlasName = String(feature.properties.atlasName ?? name);
            const selectable = Boolean(onSelect && atlasId);
            const activate = () => selectable && onSelect?.(atlasId);
            return (
              <g key={`${String(feature.properties.fid ?? index)}-${index}`}>
                <path
                  d={path}
                  fillRule="evenodd"
                  clipRule="evenodd"
                  className={`focus-shape drilldown-shape ${selectable ? "is-selectable" : "is-selected"}`}
                  tabIndex={selectable ? 0 : undefined}
                  role={selectable ? "button" : undefined}
                  aria-label={selectable ? `${atlasName}. ${prompt}` : atlasName}
                  onPointerEnter={() => setHoveredName(atlasName)}
                  onPointerLeave={() => setHoveredName("")}
                  onFocus={() => setHoveredName(atlasName)}
                  onBlur={() => setHoveredName("")}
                  onClick={activate}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") activate();
                  }}
                >
                  <title>{atlasName}</title>
                </path>
                {!selectable && <path d={path} fillRule="evenodd" clipRule="evenodd" className="map-tracer is-visible" filter={`url(#focus-glow-${instanceId})`} aria-hidden="true" />}
              </g>
            );
          })}
        </g>
        <g className="infrastructure-markers" aria-label="Mapped health facilities and police units">
          {points.map((point, index) => {
            const [x, y] = project(point.coordinates);
            return (
              <circle
                key={`${point.kind}-${point.localId}-${index}`}
                className={`infrastructure-marker is-${point.kind}`}
                cx={x}
                cy={y}
                r={point.kind === "health" ? 4.6 : 4.2}
                tabIndex={0}
                role="button"
                aria-label={`${point.name}. ${point.kind === "health" ? "Health facility" : "Police unit"}`}
              >
                <title>{point.name}{point.category ? ` · ${point.category}` : ""}</title>
              </circle>
            );
          })}
        </g>
      </svg>
      {!!points.length && <div className="point-legend"><span><i className="health-dot" />Health</span><span><i className="police-dot" />Police</span><small>{points.length} mapped points</small></div>}
    </div>
  );
}

const markerImage = (kind: "health" | "police") => {
  const canvas = document.createElement("canvas");
  canvas.width = 72;
  canvas.height = 88;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable");
  const color = kind === "health" ? "#df3f32" : "#2d7895";
  context.shadowColor = "rgba(22,42,36,.28)";
  context.shadowBlur = 8;
  context.shadowOffsetY = 5;
  context.beginPath();
  context.moveTo(36, 82);
  context.bezierCurveTo(30, 68, 12, 57, 12, 34);
  context.arc(36, 34, 24, Math.PI, 0);
  context.bezierCurveTo(60, 57, 42, 68, 36, 82);
  context.closePath();
  context.fillStyle = color;
  context.fill();
  context.shadowColor = "transparent";
  context.beginPath();
  context.arc(36, 34, 17, 0, Math.PI * 2);
  context.fillStyle = "rgba(255,255,255,.15)";
  context.fill();
  context.strokeStyle = "white";
  context.fillStyle = "white";
  context.lineWidth = 4;
  context.lineCap = "round";
  context.lineJoin = "round";
  if (kind === "health") {
    context.beginPath();
    context.moveTo(36, 23);
    context.lineTo(36, 45);
    context.moveTo(25, 34);
    context.lineTo(47, 34);
    context.stroke();
  } else {
    context.beginPath();
    context.moveTo(26, 25);
    context.lineTo(46, 25);
    context.lineTo(43, 39);
    context.quadraticCurveTo(36, 48, 29, 39);
    context.closePath();
    context.stroke();
    context.beginPath();
    context.arc(36, 31, 3, 0, Math.PI * 2);
    context.fill();
  }
  return context.getImageData(0, 0, canvas.width, canvas.height);
};

function SlippyBoundaryMap({
  boundary,
  name,
  prompt,
  level,
  onSelect,
  points,
}: {
  boundary: FeatureCollection;
  name: string;
  prompt: string;
  level: "province" | "district" | "local";
  onSelect?: (id: string) => void;
  points: InfrastructurePoint[];
}) {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!container.current) return;
    let cancelled = false;
    let map: import("maplibre-gl").Map | undefined;

    void import("maplibre-gl").then(({ default: maplibregl }) => {
      if (cancelled || !container.current) return;
      const activeMap = new maplibregl.Map({
      container: container.current,
      style: "https://tiles.openfreemap.org/styles/positron",
      center: [84, 28.25],
      zoom: 6,
      minZoom: 5,
      maxZoom: 18,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      cooperativeGestures: false,
    });
    map = activeMap;
    activeMap.touchZoomRotate.disableRotation();
    activeMap.addControl(new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }), "bottom-right");
    activeMap.addControl(new maplibregl.AttributionControl({ compact: true, customAttribution: "Census boundaries · NSO Nepal" }), "bottom-left");

    activeMap.on("load", () => {
      activeMap.addImage("health-pin", markerImage("health"), { pixelRatio: 2 });
      activeMap.addImage("police-pin", markerImage("police"), { pixelRatio: 2 });
      activeMap.addSource("census-boundary", { type: "geojson", data: boundary as never });
      activeMap.addLayer({ id: "census-boundary-fill", type: "fill", source: "census-boundary", paint: { "fill-color": "#df3f32", "fill-opacity": onSelect ? 0.1 : 0.18 } });
      activeMap.addLayer({ id: "census-boundary-line", type: "line", source: "census-boundary", paint: { "line-color": "#df3f32", "line-width": onSelect ? 2.2 : 3, "line-opacity": 0.95 } });

      const pointData = {
        type: "FeatureCollection",
        features: points.map((point, index) => ({
          type: "Feature",
          id: index,
          properties: { kind: point.kind, name: point.name, category: point.category ?? "" },
          geometry: { type: "Point", coordinates: point.coordinates },
        })),
      };
      activeMap.addSource("infrastructure", { type: "geojson", data: pointData as never, cluster: true, clusterRadius: 42, clusterMaxZoom: 15 });
      activeMap.addLayer({ id: "facility-clusters", type: "circle", source: "infrastructure", filter: ["has", "point_count"], paint: { "circle-color": ["step", ["get", "point_count"], "#3f6f60", 10, "#24483e", 40, "#df3f32"], "circle-radius": ["step", ["get", "point_count"], 12, 10, 15, 40, 19], "circle-stroke-color": "rgba(255,255,255,.95)", "circle-stroke-width": 2, "circle-opacity": 0.94 } });
      activeMap.addLayer({ id: "facility-cluster-count", type: "symbol", source: "infrastructure", filter: ["has", "point_count"], layout: { "text-field": ["get", "point_count_abbreviated"], "text-font": ["Noto Sans Regular"], "text-size": 10 }, paint: { "text-color": "#ffffff" } });
      activeMap.addLayer({ id: "facility-points", type: "symbol", source: "infrastructure", filter: ["!", ["has", "point_count"]], layout: { "icon-image": ["match", ["get", "kind"], "health", "health-pin", "police-pin"], "icon-size": ["interpolate", ["linear"], ["zoom"], 7, 0.42, 12, 0.54, 17, 0.68], "icon-anchor": "bottom", "icon-allow-overlap": true } });

      const bounds = new maplibregl.LngLatBounds();
      const addCoordinates = (coordinates: unknown) => {
        if (!Array.isArray(coordinates)) return;
        if (typeof coordinates[0] === "number" && typeof coordinates[1] === "number") bounds.extend(coordinates as [number, number]);
        else coordinates.forEach(addCoordinates);
      };
      boundary.features.forEach((feature) => addCoordinates(feature.geometry.coordinates));
      if (!bounds.isEmpty()) {
        const [[west, south], [east, north]] = bounds.toArray();
        const profile = {
          province: { longitude: 0.3, latitude: 0.26, minZoom: 5, maxZoom: 11, fitMaxZoom: 9 },
          district: { longitude: 0.26, latitude: 0.24, minZoom: 6, maxZoom: 14, fitMaxZoom: 11 },
          local: { longitude: 0.34, latitude: 0.3, minZoom: 7, maxZoom: 17, fitMaxZoom: 13 },
        }[level];
        const longitudePad = Math.max((east - west) * profile.longitude, 0.12);
        const latitudePad = Math.max((north - south) * profile.latitude, 0.09);
        const compact = window.matchMedia("(max-width: 640px)").matches;
        activeMap.setMinZoom(profile.minZoom);
        activeMap.setMaxZoom(profile.maxZoom);
        activeMap.fitBounds(bounds, {
          padding: compact ? { top: 46, right: 28, bottom: 54, left: 28 } : { top: 70, right: 62, bottom: 62, left: 62 },
          duration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 900,
          maxZoom: profile.fitMaxZoom,
        });
        activeMap.setMaxBounds([[west - longitudePad, south - latitudePad], [east + longitudePad, north + latitudePad]]);
      }

      activeMap.on("click", "facility-clusters", async (event) => {
        const feature = event.features?.[0];
        const clusterId = feature?.properties?.cluster_id;
        const source = activeMap.getSource("infrastructure") as import("maplibre-gl").GeoJSONSource;
        if (clusterId === undefined) return;
        const zoom = await source.getClusterExpansionZoom(clusterId);
        const coordinates = (feature?.geometry as { coordinates?: [number, number] })?.coordinates;
        if (coordinates) activeMap.easeTo({ center: coordinates, zoom, duration: 650 });
      });
      activeMap.on("click", "facility-points", (event) => {
        const feature = event.features?.[0];
        const coordinates = (feature?.geometry as { coordinates?: [number, number] })?.coordinates;
        if (!feature || !coordinates) return;
        const popup = document.createElement("div");
        const title = document.createElement("strong");
        const detail = document.createElement("span");
        title.textContent = String(feature.properties?.name ?? "Mapped facility");
        detail.textContent = feature.properties?.kind === "health" ? String(feature.properties?.category || "Health facility") : "Police unit";
        popup.append(title, detail);
        new maplibregl.Popup({ offset: 30, closeButton: true, className: "atlas-map-popup" }).setLngLat(coordinates).setDOMContent(popup).addTo(activeMap);
      });
      if (onSelect) activeMap.on("click", "census-boundary-fill", (event) => {
        const id = event.features?.[0]?.properties?.atlasId;
        if (id) onSelect(String(id));
      });
      ["facility-clusters", "facility-points", ...(onSelect ? ["census-boundary-fill"] : [])].forEach((layer) => {
        activeMap.on("mouseenter", layer, () => { activeMap.getCanvas().style.cursor = "pointer"; });
        activeMap.on("mouseleave", layer, () => { activeMap.getCanvas().style.cursor = ""; });
      });
    });
    });

    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [boundary, name, onSelect, points]);

  return (
    <div className="drilldown-map-wrap slippy-map-wrap">
      <div className="map-selection-prompt"><MapPin size={15} weight="fill" /><span>{prompt}</span></div>
      <div ref={container} className="slippy-map" role="application" aria-label={`${name} interactive map with census boundaries, health facilities, and police units`} />
      {!!points.length && <div className="point-legend"><span><i className="health-pin-mini">+</i>Health</span><span><i className="police-pin-mini">◆</i>Police</span><small>{points.length} mapped points</small></div>}
    </div>
  );
}

function InfrastructurePanel({
  summary,
  language,
  labels,
  methodology,
}: {
  summary: InfrastructureSummary;
  language: Language;
  labels: typeof copy.en | typeof copy.ne;
  methodology: string;
}) {
  const healthType = summary.leadingHealthTypes[0];
  const cards = [
    { icon: House, label: labels.householdCount, value: formatInfrastructure(summary.households, language), detail: labels.householdNote },
    { icon: Hospital, label: labels.healthFacilities, value: formatInfrastructure(summary.healthFacilities, language), detail: healthType?.name ?? labels.infrastructureNote },
    { icon: PoliceCar, label: labels.policeUnits, value: formatInfrastructure(summary.policeUnits, language), detail: labels.infrastructureNote },
  ];
  return (
    <section className="infrastructure-panel" aria-label={labels.infrastructureNote}>
      <div className="infrastructure-grid">
        {cards.map((card) => {
          const Icon = card.icon;
          return <article className="infrastructure-card" key={card.label}><Icon size={22} weight="duotone" /><span>{card.label}</span><strong>{card.value}</strong><p>{card.detail}</p></article>;
        })}
      </div>
      <p className="infrastructure-method"><Info size={15} />{methodology}</p>
    </section>
  );
}

const formatInfrastructure = (value: number, language: Language) =>
  new Intl.NumberFormat(language === "ne" ? "ne-NP" : "en-US", { maximumFractionDigits: 1 }).format(value);

function Metric({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {suffix && <small>{suffix}</small>}
    </div>
  );
}

function EstimatedPopulationMetric({ total, growthRate, language, label }: { total: number; growthRate: number; language: Language; label: string }) {
  const [value, setValue] = useState(total);
  const [sessionGain, setSessionGain] = useState(0);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = root.current;
    if (!node) return;
    let timer = 0;
    let activeMilliseconds = 0;
    let previous = performance.now();
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      previous = performance.now();
      timer = window.setInterval(() => {
        const now = performance.now();
        if (document.visibilityState === "visible") activeMilliseconds += now - previous;
        previous = now;
        const gained = (activeMilliseconds / 60_000) * 1_000;
        setSessionGain(gained);
        setValue(total + gained);
      }, window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 1_000 : 100);
    }, { threshold: 0.35 });
    observer.observe(node);
    return () => { observer.disconnect(); window.clearInterval(timer); };
  }, [total]);

  return (
    <div ref={root} className="metric metric-estimate">
      <span>{language === "ne" ? "प्रत्यक्ष सत्र प्रक्षेपण" : "Live session projection"}</span>
      <strong>{number(Math.round(value), language)}</strong>
      <small>{label}: {number(total, language)} · 2021</small>
      <em>{language === "ne" ? `यो सत्रमा +${number(Math.floor(sessionGain), language)} · बढीमा १,००० प्रति मिनेट · आधिकारिक प्रत्यक्ष गणना होइन` : `+${number(Math.floor(sessionGain), language)} this session · capped at 1,000/min · not an official live count`}</em>
    </div>
  );
}

export default function App() {
  const { atlas, map, infrastructure, infrastructurePoints, error } = useAtlasData();
  const params = new URLSearchParams(window.location.search);
  const [language, setLanguage] = useState<Language>((params.get("lang") as Language) || "en");
  const [selectedProvince, setSelectedProvince] = useState(params.get("province") || "");
  const [selectedDistrict, setSelectedDistrict] = useState(params.get("district") || "");
  const [selectedLocal, setSelectedLocal] = useState(params.get("local") || "");
  const [selectedWard] = useState("");
  const [drillBoundary, setDrillBoundary] = useState<FeatureCollection | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(params.get("view") === "library");
  const explorerCanvas = useRef<HTMLDivElement>(null);
  const t = copy[language];

  const province = atlas?.provinces.find((item) => item.id === selectedProvince);
  const district = province?.districts.find((item) => item.id === selectedDistrict);
  const local = district?.localLevels.find((item) => item.id === selectedLocal);

  useEffect(() => {
    const syncView = () => setLibraryOpen(new URLSearchParams(window.location.search).get("view") === "library");
    window.addEventListener("popstate", syncView);
    return () => window.removeEventListener("popstate", syncView);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    if (!selectedProvince) {
      setDrillBoundary(null);
      return () => controller.abort();
    }

    const boundaryUrl = selectedLocal
      ? `/data/maps/local-p${selectedProvince}-d${selectedDistrict}-m${selectedLocal}.geojson`
      : selectedDistrict
        ? `/data/maps/district-${selectedDistrict}-locals.geojson`
        : `/data/maps/province-${selectedProvince}-districts.geojson`;

    const loadBoundary = async () => {
      try {
        const response = await fetch(boundaryUrl, { signal: controller.signal });
        if (!response.ok) throw new Error("Boundary unavailable");
        setDrillBoundary(await response.json());
      } catch (boundaryError) {
        if (!(boundaryError instanceof DOMException && boundaryError.name === "AbortError")) setDrillBoundary(null);
      }
    };
    loadBoundary();
    return () => controller.abort();
  }, [selectedProvince, selectedDistrict, selectedLocal]);

  useEffect(() => {
    document.documentElement.lang = language;
    const next = new URL(window.location.href);
    next.searchParams.set("lang", language);
    selectedProvince ? next.searchParams.set("province", selectedProvince) : next.searchParams.delete("province");
    selectedDistrict ? next.searchParams.set("district", selectedDistrict) : next.searchParams.delete("district");
    selectedLocal ? next.searchParams.set("local", selectedLocal) : next.searchParams.delete("local");
    next.searchParams.delete("ward");
    window.history.replaceState({}, "", next);
  }, [language, selectedProvince, selectedDistrict, selectedLocal]);

  const resetProvince = (id: string) => {
    setSelectedProvince(id);
    setSelectedDistrict("");
    setSelectedLocal("");
    if (id) window.setTimeout(() => {
      const explorer = document.getElementById("explorer");
      if (explorer) window.scrollTo({ top: explorer.getBoundingClientRect().top + window.scrollY - 18, behavior: "smooth" });
    }, 80);
  };

  const resetDistrict = (id: string) => {
    setSelectedDistrict(id);
    setSelectedLocal("");
  };

  const selectLocal = (id: string) => {
    setSelectedLocal(id);
  };

  const current = local ?? district ?? province ?? atlas?.national;
  const currentName = local?.name ?? district?.name ?? (province ? province.name[language] : t.national);
  const infrastructureSummary = local && province && district
    ? infrastructure?.locals[`${province.id}-${district.id}-${local.id}`]
    : district
      ? infrastructure?.districts[district.id]
      : province
        ? infrastructure?.provinces[province.id]
        : infrastructure?.national;
  const localCount = province?.localLevelCount ?? atlas?.national.localLevelCount ?? 0;
  const wardCount = local?.wards ?? district?.wards ?? province?.wards ?? atlas?.national.wards ?? 0;
  const visibleInfrastructurePoints = useMemo(() => {
    if (!infrastructurePoints || !selectedProvince) return [];
    return infrastructurePoints.points.filter((point) =>
      point.provinceId === selectedProvince
      && (!selectedDistrict || point.districtId === selectedDistrict)
      && (!selectedLocal || point.localId === selectedLocal),
    );
  }, [infrastructurePoints, selectedProvince, selectedDistrict, selectedLocal]);

  useGSAP(() => {
    if (!explorerCanvas.current) return;
    const media = gsap.matchMedia();
    media.add("(prefers-reduced-motion: no-preference)", () => {
      const timeline = gsap.timeline({ defaults: { ease: "power2.out", overwrite: "auto" } });
      timeline
        .fromTo(".location-summary > *", { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.45, stagger: 0.04 })
        .fromTo(".infrastructure-card", { autoAlpha: 0, y: 12, scale: 0.98 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, stagger: 0.045 }, "<0.08");
    });
    return () => media.revert();
  }, { dependencies: [selectedProvince, selectedDistrict, selectedLocal], scope: explorerCanvas, revertOnUpdate: true });

  const handleShare = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const openLibrary = () => {
    const next = new URL(window.location.href);
    next.searchParams.set("view", "library");
    next.hash = "data-library";
    window.history.pushState({}, "", next);
    setLibraryOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeLibrary = () => {
    const next = new URL(window.location.href);
    next.searchParams.delete("view");
    next.hash = "top";
    window.history.pushState({}, "", next);
    setLibraryOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateTo = (target: string) => {
    if (libraryOpen) closeLibrary();
    window.setTimeout(() => { const element = document.getElementById(target); if (element) window.scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - 24, behavior: "smooth" }); }, libraryOpen ? 80 : 0);
    setMenuOpen(false);
  };

  const stepMapBack = () => {
    if (selectedLocal) selectLocal("");
    else if (selectedDistrict) resetDistrict("");
    else if (selectedProvince) resetProvince("");
  };

  const exportSummary = () => {
    const rows = [
      ["Geography", currentName],
      ["Ward", selectedWard || "All"],
      ["Population", current?.total ?? 0],
      ["Male", current?.male ?? 0],
      ["Female", current?.female ?? 0],
      ["Sex ratio", current?.sexRatio ?? 0],
      ["Source", "National Population and Housing Census 2021, NSO Nepal"],
    ];
    const csv = rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${currentName.toLowerCase().replace(/\W+/g, "-")}-summary.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <main className="state-screen">
        <Info size={32} />
        <h1>Unable to load the atlas data.</h1>
        <p>Check the generated data files and reload the page.</p>
      </main>
    );
  }

  if (!atlas || !map || !infrastructure || !infrastructurePoints) {
    return (
      <main className="state-screen" aria-live="polite">
        <div className="loader-mark" />
        <p>Preparing the open census atlas…</p>
      </main>
    );
  }

  const comparisonItems = district
    ? district.localLevels.map((item) => ({ id: item.id, name: item.name, total: item.total }))
    : province
      ? province.districts.map((item) => ({ id: item.id, name: item.name, total: item.total }))
      : atlas.provinces.map((item) => ({ id: item.id, name: item.name[language], total: item.total }));
  const maxComparison = Math.max(...comparisonItems.map((item) => item.total), 1);
  const comparisonTitle = district
    ? `${language === "ne" ? "स्थानीय तहअनुसार जनसंख्या" : "Population by local level"} · ${district.name}`
    : province
      ? `${language === "ne" ? "जिल्लाअनुसार जनसंख्या" : "Population by district"} · ${province.name[language]}`
      : t.comparison;

  return (
    <div className={language === "ne" ? "app nepali" : "app"}>
      <header className="site-header">
        <a className="wordmark" href="#top" aria-label={t.openAtlas}>
          <span className="wordmark-shape" aria-hidden="true"><span /></span>
          <span><strong>{language === "ne" ? "नेपाल खुला डाटा" : "Nepal Open Data"}</strong><small>{t.openAtlas}</small></span>
        </a>
        <nav className={menuOpen ? "main-nav is-open" : "main-nav"} aria-label="Primary navigation">
          <a href="#explorer" onClick={(event) => { event.preventDefault(); navigateTo("explorer"); }}>{t.explore}</a>
          <a href="#source" onClick={(event) => { event.preventDefault(); navigateTo("source"); }}>{t.data}</a>
          <a href="#source" onClick={(event) => { event.preventDefault(); navigateTo("source"); }}>{t.method}</a>
          <a href="#about" onClick={(event) => { event.preventDefault(); navigateTo("about"); }}>{t.about}</a>
        </nav>
        <div className="header-actions">
          <button className="language-toggle" onClick={() => setLanguage(language === "en" ? "ne" : "en")}>
            <GlobeHemisphereWest size={18} /> {language === "en" ? "नेपाली" : "English"}
          </button>
          <button className="menu-toggle" aria-label="Toggle navigation" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </header>

      <main id="top" className={libraryOpen ? "library-page" : undefined}>
        {libraryOpen ? (
          <DataExplorer atlas={atlas} province={province} district={district} local={local} language={language} selectedWard={selectedWard} fullPage onBack={closeLibrary} />
        ) : <>
        <section className="hero section-shell">
          <div className="hero-copy">
            <p className="eyebrow"><span /><span>{t.eyebrow}</span></p>
            <h1>{t.titleA}<br /><em>{t.titleB}</em></h1>
            <p className="hero-intro">{t.intro}</p>
            <div className="hero-actions">
              <a className="button primary" href="#explorer">{t.exploreData}<ArrowRight size={18} /></a>
              <a className="button secondary" href="#source">{t.browseSources}</a>
            </div>
            <p className="trust-line"><CheckCircle weight="fill" size={18} />{t.verified}</p>
          </div>
          <div className="hero-map-wrap">
            <div className="map-note"><MapPin size={16} weight="fill" /><span>{currentName}</span></div>
            <NepalMap
              map={map}
              selectedProvince={selectedProvince}
              onSelect={resetProvince}
              provinceNames={(id) => atlas.provinces.find((item) => item.id === id)?.name[language] ?? `Province ${id}`}
              instanceId="hero"
            />
            <div className="map-coordinate">26.3°–30.4° N<br />80.0°–88.2° E</div>
          </div>
        </section>

        <section className="snapshot section-shell" aria-labelledby="snapshot-title">
          <div className="section-heading">
            <div><p className="kicker">01 / 2021</p><h2 id="snapshot-title">{t.nationalSnapshot}</h2></div>
            <p>{t.snapshotBody}</p>
          </div>
          <div className="metric-grid">
            <EstimatedPopulationMetric total={atlas.national.total} growthRate={atlas.national.growthRate} language={language} label={t.population} />
            <Metric label={t.households} value={number(atlas.national.households, language)} />
            <Metric label={t.sexRatio} value={number(atlas.national.sexRatio, language)} suffix="M / 100 F" />
            <Metric label={t.density} value={number(atlas.national.density, language)} />
          </div>
        </section>

        <section id="explorer" className="explorer section-shell" aria-labelledby="explorer-title">
          <div className="explorer-topline">
            <div><p className="kicker">02 / {t.explore}</p><h2 id="explorer-title">{t.choosePlace}</h2><p>{t.chooseBody}</p></div>
            <button className="button secondary share-button" onClick={handleShare}><ShareNetwork size={18} />{copied ? t.copied : t.share}</button>
          </div>

          <div className="filter-bar">
            <SlidersHorizontal className="filter-icon" size={22} />
            <label><span>{t.province}</span><div className="select-wrap"><select value={selectedProvince} onChange={(event) => resetProvince(event.target.value)}><option value="">{t.allNepal}</option>{atlas.provinces.map((item) => <option key={item.id} value={item.id}>{item.name[language]}</option>)}</select><CaretDown size={15} /></div></label>
            <label><span>{t.district}</span><div className="select-wrap"><select disabled={!province} value={selectedDistrict} onChange={(event) => resetDistrict(event.target.value)}><option value="">{t.selectDistrict}</option>{province?.districts.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><CaretDown size={15} /></div></label>
            <label><span>{t.localLevel}</span><div className="select-wrap"><select disabled={!district} value={selectedLocal} onChange={(event) => selectLocal(event.target.value)}><option value="">{t.selectLocal}</option>{district?.localLevels.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><CaretDown size={15} /></div></label>
          </div>

          <div ref={explorerCanvas} className="explorer-canvas">
            <aside className="location-summary">
              <p className="summary-label"><MapPin size={17} />{t.locationFocus}</p>
              <p className="breadcrumb">{t.national}{province ? ` / ${province.name[language]}` : ""}{district ? ` / ${district.name}` : ""}</p>
              <h3>{currentName}</h3>
              <p className="summary-population">{number(current?.total ?? 0, language)} <span>{t.people}</span></p>
              <div className="sex-split" aria-label={`${t.male} and ${t.female}`}>
                <div style={{ width: `${((current?.male ?? 0) / (current?.total || 1)) * 100}%` }}><span><GenderMale size={15} weight="bold" />{t.male}</span><strong>{number(current?.male ?? 0, language)}</strong></div>
                <div><span><GenderFemale size={15} weight="bold" />{t.female}</span><strong>{number(current?.female ?? 0, language)}</strong></div>
              </div>
              <div className="summary-facts"><div><span>{t.sexRatio}</span><strong>{number(current?.sexRatio ?? 0, language)}</strong></div><div><span>{t.localLevels}</span><strong>{number(local ? 1 : district ? district.localLevels.length : localCount, language)}</strong></div><div><span>{t.wards}</span><strong>{number(wardCount, language)}</strong></div></div>
              <p className="ward-note"><Info size={16} />{language === "ne" ? "वडा-स्तरमा हाल जनसङ्ख्याको सारांश मात्र उपलब्ध छ; विस्तृत घरपरिवार सूचक स्थानीय तहमा उपलब्ध छन्।" : "Ward coverage currently contains population summaries only; detailed household indicators are available at local level."}</p>
            </aside>
            <div className="map-panel">
              <div className="map-panel-meta"><span><span className="status-dot" />{t.verified}</span><span>2021 · NSO Nepal</span></div>
              {selectedProvince && <button type="button" className="map-back" onClick={stepMapBack}><ArrowLeft size={16} />{selectedLocal ? district?.name : selectedDistrict ? province?.name[language] : t.national}</button>}
              {drillBoundary ? (
                <SlippyBoundaryMap
                  key={`${selectedProvince}-${selectedDistrict}-${selectedLocal || "province"}`}
                  boundary={drillBoundary}
                  name={currentName}
                  prompt={local ? t.selectedBoundary : district ? t.chooseLocalMap : t.chooseDistrictMap}
                  level={local ? "local" : district ? "district" : "province"}
                  onSelect={local ? undefined : district ? selectLocal : resetDistrict}
                  points={visibleInfrastructurePoints}
                />
              ) : (
                <NepalMap map={map} selectedProvince={selectedProvince} onSelect={resetProvince} provinceNames={(id) => atlas.provinces.find((item) => item.id === id)?.name[language] ?? `Province ${id}`} instanceId="explorer" />
              )}
              <div className="map-legend"><span><i className="legend-selected" />{t.locationFocus}</span><span><i className="legend-boundary" />{district ? t.localLevel : province ? t.district : t.provinces}</span></div>
            </div>
            {infrastructureSummary && <InfrastructurePanel summary={infrastructureSummary} language={language} labels={t} methodology={language === "ne" ? "स्वास्थ्य र प्रहरीका निर्देशाङ्क सन्दर्भ GeoJSON तहबाट लिएर २०२१ का स्थानीय तह सीमासँग स्थानिक रूपमा मिलाइएका हुन्।" : "Health and police coordinates come from the reference GeoJSON layers and were spatially matched to 2021 local-level boundaries."} />}
          </div>
        </section>

        <DataExplorer atlas={atlas} province={province} district={district} local={local} language={language} selectedWard={selectedWard} onOpenFull={openLibrary} />

        <section className="comparison section-shell" aria-labelledby="comparison-title">
          <div className="section-heading">
            <div><p className="kicker">04 / {t.coverage}</p><h2 id="comparison-title">{comparisonTitle}</h2></div>
            <p>{t.comparisonBody}</p>
          </div>
          <div className="province-bars">
            {comparisonItems.slice().sort((a, b) => b.total - a.total).map((item, index) => (
              <button key={item.id} className={(district ? selectedLocal : province ? selectedDistrict : selectedProvince) === item.id ? "province-row is-active" : "province-row"} onClick={() => district ? setSelectedLocal(item.id) : province ? resetDistrict(item.id) : resetProvince(item.id)}>
                <span className="rank">{String(index + 1).padStart(2, "0")}</span>
                <span className="province-name">{item.name}</span>
                <span className="bar-track"><span style={{ width: `${(item.total / maxComparison) * 100}%` }} /></span>
                <strong>{number(item.total, language)}</strong>
              </button>
            ))}
          </div>
        </section>

        <section id="source" className="source-section section-shell">
          <div className="source-intro"><p className="kicker">05 / {t.source}</p><h2>{t.coverage}</h2><p>{t.coverageBody}</p></div>
          <div className="coverage-grid">
            <div><GlobeHemisphereWest size={24} /><strong>7</strong><span>{t.provinces}</span></div>
            <div><MapPin size={24} /><strong>77</strong><span>{t.districts}</span></div>
            <div><Database size={24} /><strong>{number(atlas.national.localLevelCount, language)}</strong><span>{t.localLevels}</span></div>
            <div><ChartBar size={24} /><strong>{number(atlas.national.wards, language)}</strong><span>{t.wards}</span></div>
          </div>
          <div className="source-card">
            <div className="source-icon"><ShieldCheck size={28} /></div>
            <div><span>{t.source}</span><strong>{t.sourceText}</strong><small>{t.updated}: {new Date(atlas.generatedAt).toLocaleDateString(language === "ne" ? "ne-NP" : "en-US")}</small></div>
            <a href={atlas.source.url} target="_blank" rel="noreferrer">NSO Nepal <ArrowRight size={16} /></a>
          </div>
          <p className="rollup-disclosure"><Info size={16} />{language === "ne" ? "राष्ट्रिय शीर्षक संख्या २९,१६४,५७८ आधिकारिक राष्ट्रिय शृङ्खलाबाट हो। स्थानीय प्रोफाइल अभिलेखहरूको योग २८,९२५,४८० हुन्छ; दुवै शृङ्खला मिसाइएको छैन।" : "The national headline (29,164,578) comes from the official national series. The local-profile records sum to 28,925,480; the atlas keeps these source series separate instead of silently forcing them to match."}</p>
        </section>
        </>}
      </main>

      <footer id="about" className="site-footer section-shell">
        <div className="footer-mark"><span className="wordmark-shape" aria-hidden="true"><span /></span><div><strong>{t.openAtlas}</strong><p>{t.nonprofit}</p></div></div>
        <p className="creator">{t.creator}</p>
        <div className="footer-links"><a href="#source">{t.data}</a><a href="#source">{t.method}</a><button onClick={handleShare}><ShareNetwork size={17} />{t.share}</button><button onClick={openLibrary}><DownloadSimple size={17} />HTML report</button></div>
      </footer>

      {!libraryOpen && <a className="mobile-explore" href="#explorer"><SlidersHorizontal size={19} />{t.filters}</a>}
    </div>
  );
}
